import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getTrainingContainer } from '../../container/training-module'
import { IUserTrainingService, USER_TRAINING_SERVICE_KEY } from '../../modules/training'
import styles from './viewStyle.module.css'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import {
  emptyUserTrainingDTO,
  fromModel,
  UserTrainingDTO,
} from '../../modules/training/models/UserTrainingDTO'
import { navigate } from '@reach/router'
import { ROUTE_DASHBOARD_TRAINING, ROUTE_TRAINING } from '../../routes/routes-constants'
import { canCompleteTrainingNow } from '../../common/utils/date'
import { User } from '../../modules/users/models/User'
import { getUserContainer } from '../../container/user-module'
import { UserService } from '../../modules/users/services/UserService'
import { LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { getMediaContainer } from '../../container/media-module'
import { MediaService } from '../../modules/media/services/MediaService'
import { MEDIA_SERVICE_KEY } from '../../modules/media'
import { getImageSource, isImageUrl } from '../../common/utils/file'
import { PageLayout } from 'pages/layout'

type TrainingView = {
  id?: string
}

const userContainer = getUserContainer()
const mediaService = getMediaContainer().get<MediaService>(MEDIA_SERVICE_KEY)
const userService = userContainer.get<UserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const userTrainingService =
  getTrainingContainer().get<IUserTrainingService>(USER_TRAINING_SERVICE_KEY)

export function View(props: TrainingView) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [user, setUser] = useState<User>()
  const [image, setImage] = useState<string>('')
  const [userTraining, setUserTraining] = useState<UserTrainingDTO>(emptyUserTrainingDTO())
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!loggedUser?.id) return
    userService.getByID(loggedUser?.id).subscribe((res) => {
      res && setUser(res)
    })
  }, [])

  useEffect(() => {
    if (!props?.id) return
    userTrainingService.getByID(props.id).subscribe((res) => {
      if (!res) return
      const dto = fromModel(res)
      setUserTraining(dto)

      const img = dto.image ?? ''
      if (img && isImageUrl(img)) {
        setImage(img)
      } else if (img && img.length > 100) {
        setImage(img)
      } else if (dto.title) {
        mediaService.getByName(dto.title).subscribe((media) => {
          if (media) setImage(media.data)
        })
      }

      setIsLoading(false)
    })
  }, [props.id])

  const handlerCompleted = () => {
    const newUserTraining = Object.assign({ ...userTraining }, { completed: true })
    if (!props.id) return
    userTrainingService.update(newUserTraining).subscribe(() => {
      setUserTraining(newUserTraining)
    })
  }

  const goBack = () => {
    navigate(
      user?.isPatient ? ROUTE_TRAINING : `${ROUTE_DASHBOARD_TRAINING}/${userTraining.userID}`
    )
  }

  const getFrequencyLabel = (freq: number) => {
    if (freq === 1) return t('morning')
    if (freq === 2) return t('afternoon')
    if (freq === 3) return t('night')
    return ''
  }

  const imageSrc = getImageSource(image)

  if (isLoading) return <></>

  return (
    <PageLayout title={t(userTraining.title ?? '')}>
      <Box className={styles.trainingContainer}>
        <Box className={styles.description}>
          <Box>
            <p className={styles.descriptionTitle}>{`${t('description')}: `}</p>
            <p className={styles.textDescription}>{t(userTraining.description ?? '')}</p>
          </Box>
          <Box className={styles.contentData}>
            <p className={styles.dateText}>
              {userTraining && (
                <>
                  <span className={styles.titleData}>{`${t('date')}: `}</span>
                  <span>{new Date(userTraining?.startDate).toLocaleDateString()}</span>
                </>
              )}
            </p>
            <p className={styles.dateText}>
              <span className={styles.titleData}>{`${t('series')}: `}</span>
              <span>{userTraining.series}</span>
            </p>
            <p className={styles.dateText}>
              <span className={styles.titleData}>{`${t('repetitions')}: `}</span>
              <span>{userTraining.repetitions}</span>
            </p>
            <p className={styles.dateText}>
              <span className={styles.titleData}>{`${t('frequency')}: `}</span>
              <span>{getFrequencyLabel(userTraining.frecuency)}</span>
            </p>
            <Box display={'flex'}>
              <p className={styles.dateText}>
                <span className={styles.titleData}>{`${t('state')}: `}</span>
              </p>
              <p
                className={
                  userTraining?.completed ? styles.styleCompleted : styles.styledNotcompleted
                }
              >
                {userTraining.completed ? t('completed') : t('notComplete')}
              </p>
            </Box>
          </Box>
          <Box className={styles.buttons}>
            <Box style={{ margin: 8 }}>
              <AppButton
                label={t('back')}
                theme={ButtonTheme.NewPrimary}
                handler={goBack}
                type={'button'}
              />
            </Box>
            {userTraining.completed ||
            !user?.isPatient ||
            !canCompleteTrainingNow(new Date(userTraining.startDate), userTraining.frecuency) ? (
              ''
            ) : (
              <Box style={{ margin: 8 }}>
                <AppButton
                  label={t('complete')}
                  theme={ButtonTheme.NewPrimary}
                  handler={handlerCompleted}
                  type={'button'}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          {imageSrc ? (
            <img
              className={styles.trainingImage}
              src={imageSrc}
              alt={t('training')}
              onError={(e) => {
                console.error('Error cargando imagen:', imageSrc)
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : null}
        </Box>
      </Box>
    </PageLayout>
  )
}
